import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TangoLedgerQuery, TangoLedgerReport, TangoLedgerRow, TangoLedgerService } from '../../../services/tango-ledger/tango-ledger.service';
import { FINANCE_TABS } from '../finance-tabs';

@Component({
    selector: 'app-tango-ledger',
    templateUrl: './tango-ledger.component.html',
    styleUrls: ['./tango-ledger.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TangoLedgerComponent implements OnInit, OnDestroy {
  tabs = FINANCE_TABS;
  from = '';
  to = '';
  type = 'all';
  sort: 'date' | 'amount' | 'transaction' = 'date';
  direction: 'asc' | 'desc' = 'desc';
  report: TangoLedgerReport | null = null;
  loading = false;
  exporting = false;
  error = '';
  private request = 0;
  private applied: TangoLedgerQuery = { type: 'all', limit: 50, offset: 0, sort: 'date', direction: 'desc' };
  constructor(private service: TangoLedgerService) {}
  ngOnInit(): void { void this.load(this.applied); }
  ngOnDestroy(): void { this.request++; }
  async apply(): Promise<void> {
    if (this.from && this.to && this.from > this.to) {
      this.error = 'Start date must not follow end date.';
      return;
    }
    const query: TangoLedgerQuery = { type: this.type, offset: 0, limit: 50, sort: this.sort, direction: this.direction };
    if (this.from) { query.from = this.from; }
    if (this.to) { query.to = this.to; }
    await this.load(query);
  }
  async sortBy(field: 'date' | 'amount' | 'transaction'): Promise<void> {
    this.direction = this.sort === field ? (this.direction === 'asc' ? 'desc' : 'asc') : field === 'date' ? 'desc' : 'asc';
    this.sort = field;
    await this.load({ ...this.applied, sort: this.sort, direction: this.direction, offset: 0 });
  }
  sortAria(field: string): string { return this.sort !== field ? 'none' : this.direction === 'asc' ? 'ascending' : 'descending'; }
  sortIcon(field: string): string { return this.sort !== field ? '↕' : this.direction === 'asc' ? '↑' : '↓'; }
  async loadMore(): Promise<void> {
    if (!this.report || this.loading || !this.report.hasMore) { return; }
    await this.load({ ...this.applied, offset: this.report.offset + this.report.limit }, true);
  }
  async load(query: TangoLedgerQuery, append = false): Promise<void> {
    const request = ++this.request;
    this.loading = true;
    this.error = '';
    const existing = append ? this.report : null;
    if (!append) { this.report = null; }
    this.applied = { ...query };
    try {
      const result = await this.service.list(query);
      if (request !== this.request) { return; }
      const ids = new Set(existing?.rows.map(row => row.id));
      this.report = { ...result, rows: existing ? [...existing.rows, ...result.rows.filter(row => !ids.has(row.id))] : result.rows };
      this.applied = { ...query };
    } catch (_) {
      if (request === this.request) { this.error = 'Unable to load the Tango ledger. Check the filters and try again. If this continues, contact Wallit support.'; }
    } finally { if (request === this.request) { this.loading = false; } }
  }
  async exportCsv(): Promise<void> {
    if (this.exporting || this.loading || !this.report) { return; }
    this.exporting = true;
    this.error = '';
    try {
      const result = await this.service.export({ ...this.applied, offset: 0 });
      const url = URL.createObjectURL(new Blob([result.csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) { this.error = 'Unable to export the Tango report. Please try again.'; }
    finally { this.exporting = false; }
  }
  trackRow(_: number, row: TangoLedgerRow): string { return row.id; }
  money(value: number | null): string {
    return value === null ? 'Unknown' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value / 100);
  }
}
